import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Pencil, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { format } from "date-fns";
import { cn } from "../../lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { confirmDialog } from "../ui/confirm-dialog";

const Accessories = ({ axiosInstance }) => {
  const [accessories, setAccessories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [mainWarehouseId, setMainWarehouseId] = useState(null);

  const [brands, setBrands] = useState([]);
  const [isAddBrandOpen, setIsAddBrandOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState("");

  const [brandFilter, setBrandFilter] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [sortOption, setSortOption] = useState("alphabetical");

  const [isAddAccessoryOpen, setIsAddAccessoryOpen] = useState(false);
  const [isEditAccessoryOpen, setIsEditAccessoryOpen] = useState(false);
  const [isAccessoryDetailsOpen, setIsAccessoryDetailsOpen] = useState(false);
  const [selectedAccessory, setSelectedAccessory] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupAccessories, setGroupAccessories] = useState([]);

  const [newAccessories, setNewAccessories] = useState([
    { warehouse_id: '', brand: '', model: '', itemDescription: '', quantity: '' }
  ]);

  const [siNumber, setSiNumber] = useState("");
  const [drNumber, setDrNumber] = useState("");
  const [supplier, setSupplier] = useState("");
  const [deliveryDate, setDeliveryDate] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.email === 'vinsimon23@gmail.com';

  useEffect(() => {
    if (!axiosInstance) return;
    fetchWarehouses();
    fetchAccessories();
    fetchBrands();
  }, [axiosInstance]);

  const fetchWarehouses = async () => {
    try {
      const response = await axiosInstance.get('/inventory/warehouses');
      const ws = response.data || [];
      setWarehouses(ws);
      const main = ws.find(w => w.name === "Main Warehouse") || ws[0];
      setMainWarehouseId(main?.id || null);
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchAccessories = async () => {
    try {
      const response = await axiosInstance.get('/stock/accessories');
      setAccessories(response.data || []);
    } catch (error) {
      console.error("Error fetching accessories:", error);
      toast.error("Failed to fetch accessories");
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await axiosInstance.get('/stock/accessories/brands');
      setBrands(response.data || []);
    } catch (error) {
      console.error("Error fetching accessory brands:", error);
      setBrands([]);
    }
  };

  const getWarehouseName = (id) => {
    if (!id) return "Unassigned";
    const w = warehouses.find(x => x.id === id);
    return w ? w.name : "Unknown";
  };

  const handleAccessoryInputChange = (index, e) => {
    const { name, value } = e.target;
    setNewAccessories(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [name]: value };
      return next;
    });
  };

  const handleAddAccessoryRow = () => {
    setNewAccessories(prev => ([
      ...prev,
      { warehouse_id: '', brand: '', model: '', itemDescription: '', quantity: '' }
    ]));
  };

  const handleRemoveAccessoryRow = (index) => {
    setNewAccessories(prev => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const handleSaveAccessory = async () => {
    for (const row of newAccessories) {
      if (!row.brand || !row.itemDescription || !row.quantity) {
        toast.error("Please fill in required fields (Brand, Description, Quantity) for all rows");
        return;
      }
    }

    try {
      const payload = newAccessories.map(row => ({
        brand: row.brand,
        model: row.model || '-',
        itemDescription: row.itemDescription,
        quantity: parseInt(row.quantity) || 0,
        warehouse_id: row.warehouse_id || mainWarehouseId || null,
        si_number: siNumber,
        dr_number: drNumber,
        supplier: supplier,
        delivery_date: deliveryDate
      }));

      await axiosInstance.post('/stock/accessories', payload);
      toast.success("Accessories stocked-in successfully");

      setIsAddAccessoryOpen(false);
      setNewAccessories([{ warehouse_id: '', brand: '', model: '', itemDescription: '', quantity: '' }]);
      setSiNumber("");
      setDrNumber("");
      setSupplier("");
      setDeliveryDate(null);
      fetchAccessories();
    } catch (error) {
      console.error("Error saving accessories:", error);
      toast.error("Failed to stock-in accessories");
    }
  };

  const openGroupDetails = (groupRow) => {
    if (!isSuperAdmin) return;
    const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const group = filteredAccessories.filter(a =>
      normalize(a.brand) === normalize(groupRow.brand) &&
      normalize(a.model) === normalize(groupRow.model) &&
      normalize(a.itemDescription) === normalize(groupRow.itemDescription) &&
      (a.warehouse_id || 'null') === (groupRow.warehouse_id || 'null')
    );
    setSelectedGroup(groupRow);
    setGroupAccessories(group);
    setIsAccessoryDetailsOpen(true);
  };

  const handleEditAccessory = (accessory) => {
    setSelectedAccessory({ ...accessory });
    setIsEditAccessoryOpen(true);
  };

  const handleDeleteAccessory = async (id) => {
    const confirmed = await confirmDialog({
      title: "Delete Accessory",
      description: "Are you sure you want to delete this accessory entry?",
      variant: "destructive"
    });
    if (!confirmed) return;

    try {
      await axiosInstance.delete(`/stock/accessories/${id}`);
      toast.success("Accessory deleted successfully");
      await fetchAccessories();
      setGroupAccessories(prev => prev.filter(x => x.id !== id));
    } catch (error) {
      console.error("Error deleting accessory:", error);
      toast.error("Failed to delete accessory");
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedAccessory(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateAccessory = async () => {
    if (!selectedAccessory) return;
    try {
      const payload = {
        warehouse_id: selectedAccessory.warehouse_id || null,
        brand: selectedAccessory.brand,
        model: selectedAccessory.model,
        itemDescription: selectedAccessory.itemDescription,
        quantity: parseInt(selectedAccessory.quantity) || 0
      };
      await axiosInstance.put(`/stock/accessories/${selectedAccessory.id}`, payload);
      toast.success("Accessory updated successfully");
      setIsEditAccessoryOpen(false);
      setSelectedAccessory(null);
      await fetchAccessories();

      if (isAccessoryDetailsOpen && selectedGroup) {
        const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
        const fresh = (await axiosInstance.get('/stock/accessories')).data || [];
        const group = fresh.filter(a =>
          normalize(a.brand) === normalize(selectedGroup.brand) &&
          normalize(a.model) === normalize(selectedGroup.model) &&
          normalize(a.itemDescription) === normalize(selectedGroup.itemDescription) &&
          (a.warehouse_id || 'null') === (selectedGroup.warehouse_id || 'null')
        );
        setGroupAccessories(group);
      }
    } catch (error) {
      console.error("Error updating accessory:", error);
      toast.error("Failed to update accessory");
    }
  };

  const filteredAccessories = useMemo(() => {
    const searchLower = (searchFilter || '').toLowerCase();
    return accessories.filter(a => {
      const matchesBrand = brandFilter && brandFilter !== 'all' ? a.brand === brandFilter : true;
      const matchesSearch = !searchFilter ||
        (a.model?.toLowerCase().includes(searchLower) || a.itemDescription?.toLowerCase().includes(searchLower));
      return matchesBrand && matchesSearch;
    });
  }, [accessories, brandFilter, searchFilter]);

  const aggregatedAccessories = useMemo(() => {
    const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const map = filteredAccessories.reduce((acc, a) => {
      const key = `${normalize(a.warehouse_id || 'null')}-${normalize(a.brand)}-${normalize(a.model)}-${normalize(a.itemDescription)}`;
      if (!acc[key]) {
        acc[key] = { ...a, totalQuantity: 0, count: 0 };
      }
      acc[key].totalQuantity += (a.quantity || 0);
      acc[key].count += 1;
      return acc;
    }, {});
    return Object.values(map);
  }, [filteredAccessories]);

  const sortedAggregatedAccessories = useMemo(() => {
    return [...aggregatedAccessories].sort((a, b) => {
      switch (sortOption) {
        case 'largest_qty':
          return (b.totalQuantity || 0) - (a.totalQuantity || 0);
        case 'lowest_qty':
          return (a.totalQuantity || 0) - (b.totalQuantity || 0);
        case 'alphabetical':
        default:
          return (a.brand || '').localeCompare(b.brand || '') || (a.model || '').localeCompare(b.model || '');
      }
    });
  }, [aggregatedAccessories, sortOption]);

  const handleCreateBrand = async () => {
    if (!newBrandName.trim()) {
      toast.error("Please enter a brand name");
      return;
    }
    try {
      await axiosInstance.post('/stock/accessories/brands', { name: newBrandName.trim() });
      toast.success("Brand added successfully");
      setIsAddBrandOpen(false);
      setNewBrandName("");
      fetchBrands();
    } catch (error) {
      console.error("Error creating accessory brand:", error);
      toast.error("Failed to add brand");
    }
  };

  return (
    <div className="p-6 h-full flex flex-col space-y-6">
      <h2 className="text-2xl font-bold">Accessories Stock-In</h2>

      <div className="flex flex-1 flex-col md:flex-row gap-6 overflow-hidden">
        <div className="bg-white p-4 rounded-lg shadow flex flex-col md:w-[30%] overflow-hidden">
          <div className="flex gap-2 mb-4">
            <Button className="flex-1 flex items-center justify-center gap-2" onClick={() => setIsAddAccessoryOpen(true)}>
              +Accessories
            </Button>
            <Button variant="outline" className="flex-1" onClick={() => setIsAddBrandOpen(true)}>
              Add Brand
            </Button>
          </div>

          <div className="grid gap-4">
            <div className="space-y-2">
              <Label>Brand</Label>
              <Select value={brandFilter} onValueChange={setBrandFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Brand" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  {brands.map(b => (
                    <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Search</Label>
              <Input
                placeholder="Search Model or Description..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow flex flex-col flex-1 overflow-hidden">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Accessories</h3>
            <div className="w-[220px]">
              <Select value={sortOption} onValueChange={setSortOption}>
                <SelectTrigger>
                  <SelectValue placeholder="Sort By" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alphabetical">Alphabetical (A-Z)</SelectItem>
                  <SelectItem value="largest_qty">Largest Quantity</SelectItem>
                  <SelectItem value="lowest_qty">Lowest Quantity</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="border rounded-md flex-1 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Warehouse</TableHead>
                  <TableHead className="font-bold">Brand</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Quantity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedAggregatedAccessories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-4 text-gray-500">
                      No accessories found.
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedAggregatedAccessories.map((row, idx) => (
                    <TableRow
                      key={`${row.warehouse_id || 'null'}-${row.brand}-${row.model}-${idx}`}
                      className={isSuperAdmin ? "hover:bg-orange-50 cursor-pointer transition-colors" : ""}
                      onClick={() => openGroupDetails(row)}
                    >
                      <TableCell>{getWarehouseName(row.warehouse_id)}</TableCell>
                      <TableCell className="font-bold">{row.brand}</TableCell>
                      <TableCell>{row.model}</TableCell>
                      <TableCell>{row.itemDescription}</TableCell>
                      <TableCell>{row.totalQuantity}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <Dialog open={isAccessoryDetailsOpen} onOpenChange={setIsAccessoryDetailsOpen}>
        <DialogContent className="max-w-[85vw] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {selectedGroup ? `${selectedGroup.brand} ${selectedGroup.model}` : 'Accessory Details'}
            </DialogTitle>
            <DialogDescription>
              Warehouse: {selectedGroup ? getWarehouseName(selectedGroup.warehouse_id) : '-'} • Entries: {groupAccessories.length}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto mt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  {isSuperAdmin && <TableHead>Added By</TableHead>}
                  {isSuperAdmin && <TableHead>Date Created</TableHead>}
                  <TableHead>Warehouse</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>SI No.</TableHead>
                  <TableHead>DR No.</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Delivery Date</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupAccessories.map((a) => (
                  <TableRow key={a.id}>
                    {isSuperAdmin && <TableCell>{a.addedBy || '-'}</TableCell>}
                    {isSuperAdmin && <TableCell>{a.createdAt ? new Date(a.createdAt).toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' }) : '-'}</TableCell>}
                    <TableCell>{getWarehouseName(a.warehouse_id)}</TableCell>
                    <TableCell>{a.brand}</TableCell>
                    <TableCell>{a.model}</TableCell>
                    <TableCell>{a.itemDescription}</TableCell>
                    <TableCell>{a.si_number || '-'}</TableCell>
                    <TableCell>{a.dr_number || '-'}</TableCell>
                    <TableCell>{a.supplier || '-'}</TableCell>
                    <TableCell>{a.delivery_date ? new Date(a.delivery_date).toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' }) : '-'}</TableCell>
                    <TableCell>{a.quantity}</TableCell>
                    <TableCell className="text-right">
                      {isSuperAdmin ? (
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="icon" onClick={() => handleEditAccessory(a)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="destructive" size="icon" onClick={() => handleDeleteAccessory(a.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <span>-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isAddAccessoryOpen} onOpenChange={setIsAddAccessoryOpen}>
        <DialogContent className="max-w-[85vw] max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Stock-In Accessories</DialogTitle>
            <DialogDescription>Add new accessories to stock.</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="siNumber">SI No.</Label>
              <Input id="siNumber" value={siNumber} onChange={(e) => setSiNumber(e.target.value)} placeholder="Enter SI Number" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="drNumber">DR No.</Label>
              <Input id="drNumber" value={drNumber} onChange={(e) => setDrNumber(e.target.value)} placeholder="Enter DR Number" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="supplier">Supplier</Label>
              <Input id="supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Enter Supplier" />
            </div>
            <div className="grid gap-2">
              <Label>Date of Delivery</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !deliveryDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {deliveryDate ? format(deliveryDate, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={deliveryDate} onSelect={setDeliveryDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="flex-1 overflow-auto py-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Warehouse</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {newAccessories.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Select
                        value={row.warehouse_id}
                        onValueChange={(value) => handleAccessoryInputChange(index, { target: { name: 'warehouse_id', value } })}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder={mainWarehouseId ? getWarehouseName(mainWarehouseId) : "Warehouse"} />
                        </SelectTrigger>
                        <SelectContent>
                          {warehouses.map(w => (
                            <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={row.brand}
                        onValueChange={(value) => handleAccessoryInputChange(index, { target: { name: 'brand', value } })}
                      >
                        <SelectTrigger className="w-[150px]">
                          <SelectValue placeholder="Brand" />
                        </SelectTrigger>
                        <SelectContent>
                          {brands.map(b => (
                            <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        name="model"
                        value={row.model}
                        onChange={(e) => handleAccessoryInputChange(index, e)}
                        placeholder="Model"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        name="itemDescription"
                        value={row.itemDescription}
                        onChange={(e) => handleAccessoryInputChange(index, e)}
                        placeholder="Description"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        name="quantity"
                        type="number"
                        value={row.quantity}
                        onChange={(e) => handleAccessoryInputChange(index, e)}
                        placeholder="Qty"
                        className="w-[100px]"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveAccessoryRow(index)}
                        disabled={newAccessories.length === 1}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="mt-4 flex justify-end">
              <Button onClick={handleAddAccessoryRow} variant="outline" size="sm" className="flex items-center gap-2">
                <Plus className="h-4 w-4" /> Add Row
              </Button>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsAddAccessoryOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveAccessory}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditAccessoryOpen} onOpenChange={setIsEditAccessoryOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Accessory</DialogTitle>
            <DialogDescription>Update this accessory entry.</DialogDescription>
          </DialogHeader>

          {selectedAccessory && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Warehouse</Label>
                <Select
                  value={selectedAccessory.warehouse_id || ''}
                  onValueChange={(value) => setSelectedAccessory(prev => ({ ...prev, warehouse_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Warehouse" />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map(w => (
                      <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Brand</Label>
                <Input name="brand" value={selectedAccessory.brand || ''} onChange={handleEditInputChange} />
              </div>
              <div className="grid gap-2">
                <Label>Model</Label>
                <Input name="model" value={selectedAccessory.model || ''} onChange={handleEditInputChange} />
              </div>
              <div className="grid gap-2">
                <Label>Description</Label>
                <Input name="itemDescription" value={selectedAccessory.itemDescription || ''} onChange={handleEditInputChange} />
              </div>
              <div className="grid gap-2">
                <Label>Quantity</Label>
                <Input name="quantity" type="number" value={selectedAccessory.quantity || 0} onChange={handleEditInputChange} />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditAccessoryOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateAccessory}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isAddBrandOpen} onOpenChange={setIsAddBrandOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add New Brand</DialogTitle>
            <DialogDescription>Create a brand for Accessories.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="brandName">Brand Name</Label>
              <Input id="brandName" value={newBrandName} onChange={(e) => setNewBrandName(e.target.value)} placeholder="Enter Brand" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddBrandOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateBrand}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Accessories;
